import { IconImageInPicture } from '@tabler/icons-react'
import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import styles from '../../styles/admin/StudyContent.module.scss'

const AnswerImageDropzone = ({
  imageFile,
  onImageChange,
  existingImageUrl,
  iconSize = 48,
  className,
  placeholderText = 'Drag & Drop or Click to Upload',
  label = 'Image',
  showLabel = false,
  onExistingImageRemove,
  targetWidth = 480,
  targetHeight = 320,
  quality = 0.9,
}) => {
  const resizeImage = useCallback((file, maxWidth, maxHeight, quality) => {
    return new Promise((resolve) => {
      const img = new Image()

      img.onload = () => {
        // contain 방식: 비율 유지하면서 maxWidth/maxHeight 안에 맞춤 (크롭 없음)
        const ratio = Math.min(maxWidth / img.width, maxHeight / img.height, 1)
        const newWidth = Math.round(img.width * ratio)
        const newHeight = Math.round(img.height * ratio)

        const canvas = document.createElement('canvas')
        canvas.width = newWidth
        canvas.height = newHeight
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, newWidth, newHeight)

        canvas.toBlob(
          (blob) => {
            const resizedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            })
            resolve(resizedFile)
          },
          file.type,
          quality
        )

        img.src = ''
      }

      img.src = URL.createObjectURL(file)
    })
  }, [])

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0]
        if (file.type.startsWith('image/')) {
          try {
            const resizedFile = await resizeImage(
              file,
              targetWidth,
              targetHeight,
              quality
            )
            onImageChange(resizedFile)
          } catch (error) {
            console.error('이미지 리사이즈 실패:', error)
            onImageChange(file)
          }
        }
      }
    },
    [onImageChange, resizeImage, targetWidth, targetHeight, quality]
  )

  const handleRemoveImage = useCallback(
    (e) => {
      e.stopPropagation()
      if (imageFile) {
        onImageChange(null)
      } else if (existingImageUrl && onExistingImageRemove) {
        onExistingImageRemove()
      }
    },
    [imageFile, existingImageUrl, onImageChange, onExistingImageRemove]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
    },
    multiple: false,
    maxSize: 5 * 1024 * 1024,
  })

  const hasImage = imageFile || existingImageUrl

  return (
    <div className={styles.imageDropzoneContainer}>
      <div className={styles.imageDropzoneWrapper}>
        {showLabel && (
          <span style={{ fontSize: '15px', fontWeight: 700, color: '#111' }}>
            {label}
          </span>
        )}
        <div style={{ marginBottom: '12px' }}></div>

        <div style={{ position: 'relative' }}>
          {hasImage && (
            <button
              type="button"
              onClick={handleRemoveImage}
              className={styles.removeButton}
            >
              ×
            </button>
          )}

          <div
            {...getRootProps()}
            className={`${className || styles.imageDropzone} ${
              isDragActive ? styles.dragActive : ''
            }`}
          >
            <input {...getInputProps()} />

            {imageFile ? (
              <img
                src={URL.createObjectURL(imageFile)}
                alt="Preview"
                className={styles.previewImage}
              />
            ) : existingImageUrl ? (
              <img
                src={existingImageUrl}
                alt="Existing"
                className={styles.existingImage}
              />
            ) : (
              <div className={styles.uploadPlaceholder}>
                <IconImageInPicture size={iconSize} />
                <p>
                  {isDragActive ? 'Drop the files here...' : placeholderText}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AnswerImageDropzone
